/* edg-card-fit.js — tamanho uniforme das fotos nos cards de produto (docs/2026-09-25-listagem-cor-por-card.md).
   Cada foto de card é medida numa miniatura de 96 px (mesmo CDN): acha o objeto (pixels que diferem do fundo
   dos cantos) e aplica `scale` + `translate` (propriedades independentes: somam com o zoom de hover do tema,
   que usa `transform`) para o objeto ocupar a mesma fração do quadro e ficar centralizado em todos os cards.
   Fundo não uniforme (foto ambientada) → não mexe. Resultado por foto fica em sessionStorage. */
(() => {
  "use strict";
  if (window.__edgCardFit__) return;
  window.__edgCardFit__ = true;

  const TARGET = 0.78; /* maior dimensão do objeto / lado do quadro */
  const MIN_SCALE = 0.7;
  const MAX_SCALE = 1.45;
  const PROBE = 96;
  const SELECTOR = ".product-card__media > .media > img, .product-card__secondary > img, .product-card__carousel .media > img";
  const KEY = "edg-card-fit:v2:";

  const memo = new Map();

  const baseOf = (img) => {
    const src = img.currentSrc || img.getAttribute("src");
    if (!src || src.startsWith("data:")) return null;
    try {
      const url = new URL(src, location.href);
      url.searchParams.delete("width");
      url.searchParams.delete("height");
      url.searchParams.delete("crop");
      return url;
    } catch (_) {
      return null;
    }
  };

  /* Mede a foto: devolve { cx, cy, fw, fh, bg } em coordenadas normalizadas (0..1) da imagem, ou { skip } */
  const measure = (url) => {
    const id = url.pathname + "?" + (url.searchParams.get("v") || "");
    if (memo.has(id)) return memo.get(id);
    let cached = null;
    try { cached = JSON.parse(sessionStorage.getItem(KEY + id) || "null"); } catch (_) {}
    if (cached) {
      const done = Promise.resolve(cached);
      memo.set(id, done);
      return done;
    }
    const probeUrl = new URL(url.href);
    probeUrl.searchParams.set("width", String(PROBE));
    const job = new Promise((resolve) => {
      const probe = new Image();
      if (probeUrl.origin !== location.origin) probe.crossOrigin = "anonymous";
      probe.decoding = "async";
      probe.onload = () => {
        try { resolve(analyse(probe)); } catch (_) { resolve({ skip: true }); }
      };
      probe.onerror = () => resolve({ skip: true });
      probe.src = probeUrl.href;
    }).then((result) => {
      try { sessionStorage.setItem(KEY + id, JSON.stringify(result)); } catch (_) {}
      return result;
    });
    memo.set(id, job);
    return job;
  };

  const analyse = (probe) => {
    const W = probe.naturalWidth, H = probe.naturalHeight;
    if (!W || !H) return { skip: true };
    const canvas = document.createElement("canvas");
    canvas.width = W; canvas.height = H;
    const ctx = canvas.getContext("2d", { willReadFrequently: true });
    ctx.drawImage(probe, 0, 0);
    const px = ctx.getImageData(0, 0, W, H).data;
    const at = (x, y) => { const o = (y * W + x) * 4; return [px[o], px[o + 1], px[o + 2], px[o + 3]]; };

    /* Fundo = cantos. Todos transparentes → fundo transparente; senão precisam ser claros e parecidos */
    const corners = [at(1, 1), at(W - 2, 1), at(1, H - 2), at(W - 2, H - 2)];
    const transparent = corners.every((c) => c[3] < 16);
    let bg = null;
    if (!transparent) {
      if (corners.some((c) => c[3] < 250)) return { skip: true };
      bg = [0, 1, 2].map((i) => Math.round(corners.reduce((s, c) => s + c[i], 0) / 4));
      const spread = Math.max(...corners.map((c) => Math.abs(c[0] - bg[0]) + Math.abs(c[1] - bg[1]) + Math.abs(c[2] - bg[2])));
      if (spread > 24 || bg[0] + bg[1] + bg[2] < 600) return { skip: true };
    }

    const isObject = (o) => transparent
      ? px[o + 3] >= 24
      : Math.abs(px[o] - bg[0]) + Math.abs(px[o + 1] - bg[1]) + Math.abs(px[o + 2] - bg[2]) > 36;

    /* Projeção por coluna/linha. Sombra de chão = faixa rasa que se espalha para os lados: coluna só conta com
       altura relevante (>= 12% da maior coluna). Linhas quase sem corte (2%), para não perder canudo/alça fina. */
    const col = new Array(W).fill(0), row = new Array(H).fill(0);
    for (let y = 0; y < H; y++) {
      for (let x = 0; x < W; x++) {
        if (!isObject((y * W + x) * 4)) continue;
        col[x]++;
        row[y]++;
      }
    }
    const colMin = Math.max(1, Math.max(...col) * 0.12);
    const rowMin = Math.max(1, Math.max(...row) * 0.02);
    const x0 = col.findIndex((c) => c >= colMin);
    const x1 = col.length - 1 - [...col].reverse().findIndex((c) => c >= colMin);
    const y0 = row.findIndex((c) => c >= rowMin);
    const y1 = row.length - 1 - [...row].reverse().findIndex((c) => c >= rowMin);
    if (x0 < 0 || y0 < 0) return { skip: true };
    const fw = (x1 - x0 + 1) / W, fh = (y1 - y0 + 1) / H;
    if (fw * fh < 0.02) return { skip: true };
    return { cx: (x0 + x1 + 1) / 2 / W, cy: (y0 + y1 + 1) / 2 / H, fw, fh, ar: W / H, bg };
  };

  const apply = (img, m) => {
    if (!m || m.skip) return;
    const box = img.parentElement;
    const ew = img.clientWidth, eh = img.clientHeight;
    if (!ew || !eh) return;
    /* area ocupada pela imagem dentro do elemento (object-fit: contain) */
    const ear = ew / eh;
    const cw = m.ar >= ear ? 1 : m.ar / ear;
    const ch = m.ar >= ear ? ear / m.ar : 1;
    const ox = (1 - cw) / 2, oy = (1 - ch) / 2;
    const FW = m.fw * cw, FH = m.fh * ch; /* fração do objeto na largura / na altura do quadro */
    const cx = ox + m.cx * cw, cy = oy + m.cy * ch;
    let s = TARGET / Math.max(FW, FH);
    s = Math.min(MAX_SCALE, Math.max(MIN_SCALE, s));
    const tx = -s * (cx - 0.5) * 100;
    const ty = -s * (cy - 0.5) * 100;
    if (Math.abs(s - 1) < 0.03 && Math.abs(tx) < 1.5 && Math.abs(ty) < 1.5) return;
    img.style.scale = s.toFixed(3);
    img.style.translate = `${tx.toFixed(2)}% ${ty.toFixed(2)}%`;
    if (m.bg && box) box.style.backgroundColor = `rgb(${m.bg.join(",")})`;
    img.setAttribute("data-edg-fit", s.toFixed(2));
  };

  const fit = (img) => {
    const url = baseOf(img);
    if (!url) return;
    const key = url.href;
    if (img.__edgFitKey === key) return;
    img.__edgFitKey = key;
    img.style.scale = "";
    img.style.translate = "";
    measure(url).then((m) => {
      if (img.__edgFitKey !== key) return;
      if (img.clientWidth) apply(img, m);
      else requestAnimationFrame(() => apply(img, m));
    });
  };

  const scan = (root) => {
    (root || document).querySelectorAll(SELECTOR).forEach(fit);
  };

  let pending = false;
  const schedule = () => {
    if (pending) return;
    pending = true;
    requestAnimationFrame(() => { pending = false; scan(); });
  };

  const init = () => {
    scan();
    new MutationObserver((mutations) => {
      for (const m of mutations) {
        if (m.type === "attributes" || (m.addedNodes && m.addedNodes.length)) { schedule(); return; }
      }
    }).observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ["src", "srcset"] });
    window.addEventListener("resize", () => {
      document.querySelectorAll("img[data-edg-fit]").forEach((img) => { img.__edgFitKey = null; });
      schedule();
    });
  };

  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", init);
  else init();
})();
